/**
 * /locations/queensland — State hub for Queensland.
 *
 * Positions us in QLD water context (regulations, common sources,
 * common concerns). Lists active regions (Sunshine Coast pilot for
 * now) plus the mail-out pathway for the rest of the state.
 */
import { Navigate, Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Send, Droplets, FlaskConical, ShieldCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SchemaJsonLd, breadcrumbSchema } from "@/lib/seo";
import { useSEO, SEO_DEFAULTS, buildFaqPageLd } from "@/lib/useSEO";
import { STATES, REGIONS } from "@/data/locationTree";
import RegionPartnerChip from "@/pages/locations/RegionPartnerChip";

const BASE_URL = SEO_DEFAULTS.baseUrl;

export default function StateHub() {
  // Route is either `/locations/:slug` (via LocationTopLevelRouter) or
  // `/locations/:state` (direct hit for legacy compat). Read both.
  const params = useParams();
  const stateSlug = (params.state || params.slug || "").toLowerCase();
  const state = STATES[stateSlug];
  const url = `${BASE_URL}/locations/${stateSlug}`;

  useSEO({
    title: state
      ? `Water Testing ${state.name} — Aware Water Testing`
      : "Water Testing — Aware Water Testing",
    description: state
      ? `Independent, laboratory-certified water testing across ${state.name}. Coverage in ${state.regions.length} active region${state.regions.length === 1 ? "" : "s"} plus Australia-wide mail-out kits.`
      : undefined,
    url,
    noindex: !state,
    jsonLd: state?.extended?.faqs ? buildFaqPageLd(
      state.extended.faqs.map((f) => ({ question: f.question, answer: f.answer })),
    ) : null,
  });

  if (!state) {
    return <Navigate to="/locations/australia" replace />;
  }

  const activeRegions = state.regions
    .map((rSlug) => REGIONS[rSlug])
    .filter(Boolean);

  const breadcrumbs = [
    { name: "Home", url: BASE_URL },
    { name: "Locations", url: `${BASE_URL}/locations` },
    { name: "Australia", url: `${BASE_URL}/locations/australia` },
    { name: state.name, url },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`state-hub-${state.slug}`}>
      <Header />
      <SchemaJsonLd data={breadcrumbSchema(breadcrumbs)} />

      {/* Hero */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/locations" className="hover:text-slate-900">Locations</Link>
            <span className="mx-2">/</span>
            <Link to="/locations/australia" className="hover:text-slate-900">Australia</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-900 font-medium">{state.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-3 py-1 text-xs font-semibold mb-4">
            <MapPin className="w-3.5 h-3.5" /> {state.name} · {state.abbr}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900" data-testid="state-hub-h1">
            Water testing across {state.name}.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">{state.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book-water-test">
              <Button className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6" data-testid="state-hub-book-cta">
                Book a {state.name} test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/mail-out-water-testing-kits">
              <Button variant="outline" className="rounded-full h-11 px-6" data-testid="state-hub-mailout-cta">
                <Send className="w-4 h-4 mr-2" /> Mail-out kit anywhere in {state.abbr}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Active regions */}
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-slate-800">Active regions</h2>
            <div className="text-xs text-slate-500">
              {activeRegions.length} live · more coming through 2026
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeRegions.map((r) => {
              const regionHref = `/locations/${state.slug}/${r.slug}`;
              return (
                <div
                  key={r.slug}
                  data-testid={`state-hub-region-${r.slug}`}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-md transition p-6"
                >
                  <Link to={regionHref} className="block">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
                          Active region · Postcodes {r.postcodes_covered_range}
                        </div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">{r.name}</div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition" />
                    </div>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-3">{r.tagline}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {r.clusters.slice(0, 3).map((c) => (
                        <span key={c.key} className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                          {c.name}
                        </span>
                      ))}
                    </div>
                  </Link>
                  {/* Direct-to-partner chip — mirrors the LocationsHub. */}
                  <RegionPartnerChip
                    state={state}
                    region={r}
                    regionHubHref={regionHref}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* State context */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-cyan-700" /> Dominant water sources
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {state.context.dominant_sources.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-cyan-700">•</span>{s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-cyan-700" /> Common concerns we see
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {state.context.common_concerns.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-cyan-700">•</span>{s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-cyan-700" /> Regulatory framework
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {state.context.regulations.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-cyan-700">•</span>{s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Extended state content — only rendered for states that carry a
          genuine `extended` block (Jun 2026 GSC audit: thin state hubs). */}
      {state.extended && (
        <StateHubExtended state={state} />
      )}

      <Footer />
    </div>
  );
}

/** Prose + process + tests + guides + FAQ for a state with real copy. */
function StateHubExtended({ state }) {
  const ext = state.extended;
  return (
    <>
      <section className="border-b border-slate-100" data-testid={`state-hub-about-${state.slug}`}>
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-lg font-bold text-slate-900">
            Private water testing in {state.name}
          </h2>
          {ext.intro.map((p, i) => (
            <p key={i} className="mt-4 text-base text-slate-700 leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      <section className="border-b border-slate-100 bg-slate-50" data-testid={`state-hub-process-${state.slug}`}>
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-lg font-bold text-slate-900">
            How a {state.name} water test works
          </h2>
          <ol className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {ext.process.map((s, i) => (
              <li key={i} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="text-xs font-bold text-cyan-700">STEP {i + 1}</div>
                <div className="mt-1 text-base font-bold text-slate-900">{s.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-slate-100" data-testid={`state-hub-tests-${state.slug}`}>
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-lg font-bold text-slate-900">
            Test options for {state.name} properties
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {ext.tests.map((t) => (
              <Link key={t.href} to={t.href}
                data-testid={`state-hub-test-${t.href.replace("/", "")}`}
                className="group rounded-2xl border border-slate-200 hover:border-cyan-300 hover:shadow-md transition p-6">
                <div className="text-base font-bold text-slate-900 group-hover:text-cyan-700">{t.name}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.body}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-cyan-700">
                  View this test <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-100 bg-slate-50" data-testid={`state-hub-guides-${state.slug}`}>
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-lg font-bold text-slate-900">
            Guides for {state.name} property owners
          </h2>
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {ext.guides.map((g) => (
              <li key={g.href}>
                <Link to={g.href} className="text-sm font-medium text-cyan-800 hover:text-cyan-950 underline decoration-cyan-200 hover:decoration-cyan-500">
                  {g.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-b border-slate-100" data-testid={`state-hub-faq-${state.slug}`}>
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-lg font-bold text-slate-900">
            {state.name} water testing questions
          </h2>
          <dl className="mt-6 divide-y divide-slate-100">
            {ext.faqs.map((f, i) => (
              <div key={i} className="py-5">
                <dt className="text-base font-bold text-slate-900">{f.question}</dt>
                <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
